<template>
  <div :class="[componentClass.root, classes]">
    <div
      v-if="timeIndicatorVisible"
      :class="componentClass.element('timeIndicator')"
      :style="{ left: timeInPercent + '%' }"
    ></div>

    <template v-if="backgroundMarkersVisible">
      <PmDayBackgroundMarkerPure
        v-for="(backgroundMarker, index) in backgroundMarkersNormalized"
        :key="`${index}.${backgroundMarker.startTime.getTime()}.${backgroundMarker.endTime.getTime()}`"
        :start-time="backgroundMarker.startTime"
        :end-time="backgroundMarker.endTime"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  isToday,
  getHours,
  getMinutes,
  isSaturday,
  isSunday,
  getDay,
} from 'date-fns'
import { useComponentClass } from '@thomasaull-shared/composables'
import PmDayBackgroundMarkerPure from '@/components/persoplan/Day/PmDayBackgroundMarkerPure.vue'
import { useTime } from '@/pinia/time'

export interface BackgroundMarker {
  startTime: Date
  endTime: Date
}

export interface OpeningHours extends BackgroundMarker {
  dayOfTheWeek: number
}

export interface Props {
  date: Date
  isPublicHolidayInGermany?: boolean
  isPublicHolidayInLuxemburg?: boolean
  backgroundMarkers?: BackgroundMarker[]
  coreHours?: BackgroundMarker[]
  openingHours?: OpeningHours[]
}

const props = withDefaults(defineProps<Props>(), {
  backgroundMarkers: () => [],
  coreHours: () => [],
  openingHours: () => [],
})

const componentClass = useComponentClass({ props })
const time = useTime()

const timeInPercent = computed(() => {
  const percent =
    (100 / (24 * 60)) * (getHours(time.now) * 60 + getMinutes(time.now))

  return percent
})

const classes = computed(() => {
  return {
    'is-holiday': isHoliday.value,
    'is-saturday': isSaturday(props.date),
    'is-sunday': isSunday(props.date),
  }
})

const backgroundMarkersNormalized = computed(() => {
  return [
    ...props.backgroundMarkers,
    ...coreHoursNormalized.value,
    ...openingHoursNormalized.value,
  ]
})

const backgroundMarkersVisible = computed(() => {
  if (props.isPublicHolidayInGermany) return false
  return true
})

const openingHoursNormalized = computed(() => {
  // dateFns day of the week 1 is a monday, 0 is a sunday
  // We want to use day of the week 1 as a monday, 7 as a sunday
  const day = getDay(props.date)
  const dayOfTheWeek = day === 0 ? 7 : day

  return props.openingHours.filter((item) => item.dayOfTheWeek === dayOfTheWeek)
})

const coreHoursNormalized = computed(() => {
  if (!isOpenToday.value) return []
  return props.coreHours
})

const timeIndicatorVisible = computed(() => {
  return isToday(props.date)
})

const isOpenToday = computed(() => {
  return !isSaturday(props.date) && !isSunday(props.date)
})

const isHoliday = computed(() => {
  if (isSunday(props.date)) return true
  if (props.isPublicHolidayInGermany) return true
  if (props.isPublicHolidayInLuxemburg) return true

  return false
})
</script>

<style lang="scss">
.PmDayPure {
  $block: &;

  flex: none;
  pointer-events: none;
  background-color: #fff;
  // outline: 1px solid color.$gray-300--alpha;
  box-shadow: inset 0 0 0 0.5px color.$gray-300--alpha;
  position: relative;

  &.is-saturday {
    background-color: color.$gray-100;
  }

  &.is-holiday {
    background-color: color.$gray-200;
  }

  &-timeIndicator {
    position: absolute;
    width: 2px;
    height: 100%;
    left: 0;
    background-color: rgba(color.$primary-500, 0.75);
    z-index: 1;

    &::after {
      content: '';
      width: 50px;
      height: 100%;
      position: absolute;
      top: 0;
      right: 0;
      background: linear-gradient(
        90deg,
        rgba(color.$key, 0),
        rgba(color.$key, 0.1)
      );
    }
  }
}
</style>
